import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Search,
    Heart,
    Bone,
    Brain,
    Flame,
    Shield,
    Droplets,
    Wheat,
    Activity,
    Sparkles,
    Eye,
    Dna
} from 'lucide-react';

// 11 Autoimmune Conditions
const conditions = [
    {
        id: 'lupus',
        name: 'Lupus (SLE)',
        description: 'Systemic lupus erythematosus - affects multiple organ systems',
        icon: Sparkles,
        color: 'from-purple-500 to-pink-500',
        bgColor: 'bg-purple-100 dark:bg-purple-900/30',
        textColor: 'text-purple-700 dark:text-purple-300'
    },
    {
        id: 'rheumatoid-arthritis',
        name: 'Rheumatoid Arthritis',
        description: 'Chronic inflammatory disorder affecting joints',
        icon: Bone,
        color: 'from-orange-500 to-red-500',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        textColor: 'text-orange-700 dark:text-orange-300'
    },
    {
        id: 'multiple-sclerosis',
        name: 'Multiple Sclerosis (MS)',
        description: 'Affects the brain and spinal cord',
        icon: Brain,
        color: 'from-blue-500 to-cyan-500',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        textColor: 'text-blue-700 dark:text-blue-300'
    },
    {
        id: 'hashimotos',
        name: "Hashimoto's Thyroiditis",
        description: 'Autoimmune condition affecting the thyroid',
        icon: Shield,
        color: 'from-teal-500 to-emerald-500',
        bgColor: 'bg-teal-100 dark:bg-teal-900/30',
        textColor: 'text-teal-700 dark:text-teal-300'
    },
    {
        id: 'graves-disease',
        name: "Graves' Disease",
        description: 'Overactive thyroid (hyperthyroidism)',
        icon: Flame,
        color: 'from-amber-500 to-orange-500',
        bgColor: 'bg-amber-100 dark:bg-amber-900/30',
        textColor: 'text-amber-700 dark:text-amber-300'
    },
    {
        id: 'ibd',
        name: "Crohn's / Ulcerative Colitis",
        description: 'Inflammatory bowel diseases (IBD)',
        icon: Activity,
        color: 'from-rose-500 to-pink-500',
        bgColor: 'bg-rose-100 dark:bg-rose-900/30',
        textColor: 'text-rose-700 dark:text-rose-300'
    },
    {
        id: 'celiac',
        name: 'Celiac Disease',
        description: 'Immune reaction to eating gluten',
        icon: Wheat,
        color: 'from-yellow-500 to-amber-500',
        bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
        textColor: 'text-yellow-700 dark:text-yellow-300'
    },
    {
        id: 'psoriasis',
        name: 'Psoriasis / Psoriatic Arthritis',
        description: 'Skin condition that can affect joints',
        icon: Droplets,
        color: 'from-indigo-500 to-purple-500',
        bgColor: 'bg-indigo-100 dark:bg-indigo-900/30',
        textColor: 'text-indigo-700 dark:text-indigo-300'
    },
    {
        id: 'type1-diabetes',
        name: 'Type 1 Diabetes',
        description: 'Autoimmune condition affecting insulin production',
        icon: Dna,
        color: 'from-sky-500 to-blue-500',
        bgColor: 'bg-sky-100 dark:bg-sky-900/30',
        textColor: 'text-sky-700 dark:text-sky-300'
    },
    {
        id: 'sjogrens',
        name: "Sjögren's Syndrome",
        description: 'Affects moisture-producing glands',
        icon: Eye,
        color: 'from-cyan-500 to-teal-500',
        bgColor: 'bg-cyan-100 dark:bg-cyan-900/30',
        textColor: 'text-cyan-700 dark:text-cyan-300'
    },
    {
        id: 'ankylosing-spondylitis',
        name: 'Ankylosing Spondylitis',
        description: 'Inflammatory arthritis affecting the spine',
        icon: Heart,
        color: 'from-red-500 to-rose-500',
        bgColor: 'bg-red-100 dark:bg-red-900/30',
        textColor: 'text-red-700 dark:text-red-300'
    }
];

export default function KnowledgeBase() {
    const [searchQuery, setSearchQuery] = useState('');
    const navigate = useNavigate();

    const filteredConditions = conditions.filter(condition =>
        condition.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        condition.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-fade-in" data-testid="knowledge-base">
            {/* Header */}
            <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold">Knowledge Base</h1>
                <p className="text-muted-foreground mt-1">
                    Learn about autoimmune conditions, symptoms, testing, and management strategies
                </p>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                    placeholder="Search conditions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="condition-search"
                />
            </div>

            {/* Conditions Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConditions.map((condition) => {
                    const Icon = condition.icon;
                    return (
                        <Card
                            key={condition.id}
                            className="card-hover cursor-pointer group overflow-hidden"
                            onClick={() => navigate(`/knowledge/${condition.id}`)}
                            data-testid={`condition-${condition.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-xl ${condition.bgColor} flex items-center justify-center flex-shrink-0 group-hover:scale-110 duration-300`}>
                                        <Icon className={`w-6 h-6 ${condition.textColor}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-heading font-semibold text-lg group-hover:text-primary duration-200">
                                            {condition.name}
                                        </h3>
                                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                            {condition.description}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {filteredConditions.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-muted-foreground">No conditions found matching "{searchQuery}"</p>
                </div>
            )}

            {/* Info Banner */}
            <Card className="bg-primary/5 border-primary/20">
                <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Shield className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold">Medical Disclaimer</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                This information is for educational purposes only and is not a substitute for professional medical advice. 
                                Always consult with your healthcare provider for diagnosis and treatment options.
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// Export conditions for use in other components
export { conditions };
